import { useHostName, useDataTableContext } from "../../../../hooks";
import { BasicTableProps } from "../types";
import React, { useEffect } from "react";

const BasicTable: React.FC<BasicTableProps> = ({
	data,
	headers,
	quantityLabel,
	quantityLabelClasses,
	tableWidth,
	tableContainerHeight,
	tableContainerWidth,
	tableBottomBorderClasses,
	headerTextColor,
	headerFontSize,
	headerBorderColor,
	headerBorderWidth,
	headerFontFamily,
	tableBorderColor,
	tableBorderWidth,
	rowBackgroundColor,
	rowBorderWidth,
	rowBorderColor,
	rowHoverBackgroundColor,
	rowHoverCursor,
	rowDisabledClasses,
	cellFontFamily,
	cellFontSize,
	cellTextColor,
	cellPadding,
	hasHeaderSpan = false,
	headerSpanText,
	noDataMessage = "No Data Found",
	showQuantity,
	dynamicModalChange,
	columnModalButtonClasses = "text-primary hover:cursor-pointer hover:underline",
}) => {
	const hostName = useHostName();
	const { setModalTableOpened, setActiveUuid, activeUuid } =
		useDataTableContext();

	const errorMessageContainerClassNames =
		"min-h-[250px] flex justify-center items-center";

	const handleHeaderToggleModal = (activeId: string) => {
		if (hostName?.hostName === "COMPASS") return;
		setActiveUuid(activeId);
		setModalTableOpened(true);
		dynamicModalChange("item-fulfillments-for-sales-orders", activeId);
	};

	const handleToggleItemModal = (activeRowId: string | undefined) => {
		if (hostName?.hostName === "COMPASS") return;
		if (!activeRowId) return;
		setActiveUuid(activeRowId);
		setModalTableOpened(true);
		dynamicModalChange("item-fulfillments-for-sales-order-items");
		localStorage.setItem("modalUuid", activeRowId);
	};

	useEffect(() => {
		if (activeUuid) {
			localStorage.setItem("parentUuid", activeUuid);
		}
	}, [activeUuid]);

	let tbodyContent;
	switch (true) {
		case data?.length === 0:
			tbodyContent = (
				<tr>
					<td colSpan={headers.length}>
						<p className={errorMessageContainerClassNames}>
							{noDataMessage}
						</p>
					</td>
				</tr>
			);
			break;
		default:
			tbodyContent = data?.map((row, index) => {
				// Create a key that is a string or a number.
				const rowKey =
					typeof row.uuid === "string" || typeof row.uuid === "number"
						? row.uuid
						: index;
				const isRowModalEnabled = row.isRowModalEnabled;

				return (
					<tr
						key={rowKey}
						className={`h ${
							row?.isDisabled
								? rowDisabledClasses
								: `${rowBackgroundColor} hover:${rowHoverBackgroundColor} ${rowHoverCursor}`
						}`}
					>
						{headers?.map((header) => {
							const columnModalButtonTextClasses =
								isRowModalEnabled && header.columnHasModalButton
									? columnModalButtonClasses
									: "text-gray-700";

							return (
								<td
									key={`${rowKey}-${header.id}`}
									onClick={
										isRowModalEnabled &&
										header.columnHasModalButton
											? () =>
													handleToggleItemModal(
														row.uuid as string
													)
											: undefined
									}
									className={`text-left text-nowrap break-words ${columnModalButtonTextClasses} ${rowBorderWidth} ${rowBorderColor} ${cellFontFamily} ${cellFontSize} ${cellTextColor} ${cellPadding}`}
								>
									{row[header.id]}
								</td>
							);
						})}
					</tr>
				);
			});
	}

	return (
		<>
			<div
				className={`mb-4 ${tableContainerHeight} ${tableContainerWidth} ${tableBottomBorderClasses}`}
			>
				<table
					className={`table ${tableWidth} ${tableBorderWidth} ${tableBorderColor}`}
				>
					<thead
						className={`w-full ${
							hasHeaderSpan ? "sticky top-0 z-10" : ""
						}`}
					>
						{hasHeaderSpan && (
							<tr className="bg-white border-0">
								<th className="bg-white" colSpan={5}></th>
								<th
									colSpan={3}
									className="bg-navy-100 p-0 relative text-sm items-center justify-center text-navy-500 text-center"
								>
									<div className="border-2 border-b-0 mx-[-1px] py-1 rounded-t-md">
										<div className="after-border-top">
											{headerSpanText}
										</div>
									</div>
								</th>
								<th className="bg-white" colSpan={4}></th>
							</tr>
						)}
						<tr>
							{headers.map((header) => {
								const columnModalButtonTextClasses =
									header.columnHasModalButton
										? columnModalButtonClasses
										: "text-gray-700";

								const clickHandler = header.columnHasModalButton
									? () =>
											handleHeaderToggleModal(
												activeUuid || ""
											)
									: undefined;

								return (
									<th
										onClick={clickHandler}
										key={header.id}
										className={`py-3 m-0 text-nowrap ${columnModalButtonTextClasses} ${
											hasHeaderSpan
												? "after-border-bottom"
												: ""
										} ${headerBorderColor} ${headerBorderWidth} border-gray-500 bg-gray-200 border-r-2 ${headerTextColor} ${headerFontFamily} ${headerFontSize} ${
											header.headerClasses
										}`}
									>
										{header.Header}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>{tbodyContent}</tbody>
				</table>
			</div>
			{showQuantity && (
				<div className="flex items-start">
					<p className={quantityLabelClasses}>
						{data?.length} {quantityLabel}
					</p>
				</div>
			)}
		</>
	);
};

export default BasicTable;
